import { Helmet } from "react-helmet-async";

const DEFAULT_TITLE = "CONFILEX — Vérifier vos documents avant toute consultation juridique, fiscale ou patrimoniale";
const DEFAULT_DESC = "CONFILEX produit une lecture factuelle de vos documents (bulletin de paie, bail, facture, avis d'imposition) et vous oriente vers un réseau sélectif d'avocats, experts-comptables, notaires et conseillers en gestion de patrimoine en France.";
const DEFAULT_IMG = "https://confilex.fr/og-image.jpg";
const SITE_NAME = "CONFILEX";

// Base URL canonique : env > window.location.origin (sans trailing slash)
export const PUBLIC_BASE_URL = (
  process.env.REACT_APP_PUBLIC_URL ||
  (typeof window !== "undefined" ? window.location.origin : "https://confilex.fr")
).replace(/\/+$/, "");

/**
 * Construit une URL canonique propre :
 * - sans paramètres de tracking (utm_*, fbclid, gclid, paid, cancel, session_id…)
 * - sans trailing slash sauf pour la racine
 */
function buildCanonical(pathOrUrl) {
  let path = pathOrUrl;
  if (!path && typeof window !== "undefined") path = window.location.pathname + window.location.search;
  if (!path) return `${PUBLIC_BASE_URL}/`;
  if (path.startsWith("http")) return path;
  // Strip tracking + transactional query params
  try {
    const u = new URL(path, PUBLIC_BASE_URL);
    const drop = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "fbclid", "gclid", "mc_eid", "session_id", "paid", "cancel", "token"];
    drop.forEach((k) => u.searchParams.delete(k));
    let out = u.pathname + (u.search ? `?${u.searchParams.toString()}` : "");
    if (out !== "/" && out.endsWith("/")) out = out.slice(0, -1);
    return `${PUBLIC_BASE_URL}${out}`;
  } catch {
    return `${PUBLIC_BASE_URL}${path.startsWith("/") ? path : `/${path}`}`;
  }
}

export default function Seo({
  title,
  description,
  canonical,
  image,
  type = "website",
  noindex = false,
  jsonLd,
  keywords,
  locale = "fr_FR",
  article,
}) {
  const fullTitle = title ? `${title} — ${SITE_NAME}` : DEFAULT_TITLE;
  const desc = (description || DEFAULT_DESC).slice(0, 200);
  const img = image || DEFAULT_IMG;
  const url = buildCanonical(canonical);
  const ldArray = Array.isArray(jsonLd) ? jsonLd : (jsonLd ? [jsonLd] : []);

  return (
    <Helmet prioritizeSeoTags>
      <html lang="fr" />
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {keywords?.length > 0 && <meta name="keywords" content={keywords.join(", ")} />}
      {noindex
        ? <meta name="robots" content="noindex, nofollow" />
        : <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />}
      <link rel="canonical" href={url} />
      <link rel="alternate" href={url} hrefLang="fr-FR" />
      <link rel="alternate" href={url} hrefLang="fr" />
      <link rel="alternate" href={url} hrefLang="x-default" />

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={img} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={locale} />

      {/* Article-specific OG */}
      {article?.published_at && <meta property="article:published_time" content={article.published_at} />}
      {article?.updated_at && <meta property="article:modified_time" content={article.updated_at} />}
      {article?.author_name && <meta property="article:author" content={article.author_name} />}

      {/* Twitter / X */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@confilexfr" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={img} />

      {/* JSON-LD */}
      {ldArray.map((ld, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(ld)}</script>
      ))}
    </Helmet>
  );
}

// ---------------------------------------------------------------------------
// JSON-LD schema builders — all anchored on PUBLIC_BASE_URL (never window)
// ---------------------------------------------------------------------------

export const ldOrganization = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${PUBLIC_BASE_URL}/#organization`,
  name: SITE_NAME,
  url: PUBLIC_BASE_URL,
  logo: `${PUBLIC_BASE_URL}/favicon.ico`,
  description: DEFAULT_DESC,
  foundingDate: "2025",
  areaServed: { "@type": "Country", name: "France" },
  knowsLanguage: ["fr", "en"],
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "customer support",
    areaServed: "FR",
    availableLanguage: ["French"],
  },
  sameAs: [],
});

export const ldWebsite = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${PUBLIC_BASE_URL}/#website`,
  name: SITE_NAME,
  url: PUBLIC_BASE_URL,
  publisher: { "@id": `${PUBLIC_BASE_URL}/#organization` },
  inLanguage: "fr-FR",
  potentialAction: {
    "@type": "SearchAction",
    target: { "@type": "EntryPoint", urlTemplate: `${PUBLIC_BASE_URL}/experts?q={search_term_string}` },
    "query-input": "required name=search_term_string",
  },
});

export const ldBreadcrumb = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: it.url ? (it.url.startsWith("http") ? it.url : `${PUBLIC_BASE_URL}${it.url}`) : undefined,
  })),
});

export const ldArticle = (a) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: a.title,
  description: a.seo_meta_description || a.excerpt,
  image: a.cover_image ? [a.cover_image] : undefined,
  datePublished: a.published_at,
  dateModified: a.updated_at || a.published_at,
  author: {
    "@type": "Person",
    name: a.author_name || "Comité éditorial CONFILEX",
  },
  publisher: {
    "@type": "Organization",
    name: SITE_NAME,
    logo: { "@type": "ImageObject", url: `${PUBLIC_BASE_URL}/favicon.ico` },
  },
  mainEntityOfPage: {
    "@type": "WebPage",
    "@id": `${PUBLIC_BASE_URL}/insights/${a.slug}`,
  },
  keywords: (a.seo_keywords || []).join(", "),
  inLanguage: "fr-FR",
});

export const ldFAQ = (items) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: (items || []).map((it) => ({
    "@type": "Question",
    name: it.q,
    acceptedAnswer: { "@type": "Answer", text: it.a },
  })),
});

export const ldPerson = (e) => ({
  "@context": "https://schema.org",
  "@type": "Person",
  name: `${e.first_name} ${e.last_name}`,
  jobTitle: e.profession,
  worksFor: e.company ? { "@type": "Organization", name: e.company } : undefined,
  address: e.city ? { "@type": "PostalAddress", addressLocality: e.city, addressCountry: "FR" } : undefined,
  url: `${PUBLIC_BASE_URL}/experts/${e.id}`,
  image: e.photo,
  knowsLanguage: e.languages,
  description: e.description,
});

export const ldLocalBusiness = (profession, label, city) => ({
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  name: city ? `${label} à ${city} — CONFILEX` : `Réseau ${label} — CONFILEX`,
  description: `Sélection de ${label.toLowerCase()}s référencés par CONFILEX${city ? ` à ${city}` : ""}.`,
  areaServed: city || "France",
  url: city
    ? `${PUBLIC_BASE_URL}/specialistes/${profession}/${city.toLowerCase().replace(/\s+/g, "-")}`
    : `${PUBLIC_BASE_URL}/specialistes/${profession}`,
});

// NEW — Product/Service schema for the Vérificateur paywall (29/49/89€ TTC)
export const ldVerifierProduct = () => ({
  "@context": "https://schema.org",
  "@type": "Product",
  name: "Vérificateur CONFILEX — Rapport d'analyse documentaire",
  description: "Analyse factuelle automatisée d'un document (bulletin de paie, bail, facture, avis d'imposition) avec score de complétude, anomalies arithmétiques, informations manquantes et questions à préparer pour un professionnel qualifié.",
  brand: { "@type": "Brand", name: SITE_NAME },
  image: DEFAULT_IMG,
  category: "Document analysis service",
  offers: {
    "@type": "AggregateOffer",
    priceCurrency: "EUR",
    lowPrice: "29",
    highPrice: "89",
    offerCount: 3,
    availability: "https://schema.org/InStock",
    offers: [
      { "@type": "Offer", name: "Rapport simple", price: "29", priceCurrency: "EUR", availability: "https://schema.org/InStock", url: `${PUBLIC_BASE_URL}/verifier` },
      { "@type": "Offer", name: "Rapport standard", price: "49", priceCurrency: "EUR", availability: "https://schema.org/InStock", url: `${PUBLIC_BASE_URL}/verifier` },
      { "@type": "Offer", name: "Rapport approfondi", price: "89", priceCurrency: "EUR", availability: "https://schema.org/InStock", url: `${PUBLIC_BASE_URL}/verifier` },
    ],
  },
});

// NEW — Service schema (LegalService-adjacent but neutral, since CONFILEX n'est PAS un cabinet)
export const ldService = (opts = {}) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name: opts.name || "CONFILEX — Mise en relation et structuration documentaire",
  description: opts.description || DEFAULT_DESC,
  provider: { "@id": `${PUBLIC_BASE_URL}/#organization` },
  areaServed: { "@type": "Country", name: "France" },
  serviceType: opts.serviceType || "Document analysis and professional referral",
  url: opts.url || PUBLIC_BASE_URL,
});

// NEW — Pricing OfferCatalog
export const ldPricingCatalog = () => ({
  "@context": "https://schema.org",
  "@type": "OfferCatalog",
  name: "Tarifs CONFILEX",
  itemListElement: [
    {
      "@type": "OfferCatalog",
      name: "Vérificateur (TTC)",
      itemListElement: [
        { "@type": "Offer", name: "Rapport simple", price: "29", priceCurrency: "EUR" },
        { "@type": "Offer", name: "Rapport standard", price: "49", priceCurrency: "EUR" },
        { "@type": "Offer", name: "Rapport approfondi", price: "89", priceCurrency: "EUR" },
      ],
    },
    {
      "@type": "OfferCatalog",
      name: "Référencement spécialistes (mensuel HT)",
      itemListElement: [
        { "@type": "Offer", name: "Standard", price: "189", priceCurrency: "EUR" },
        { "@type": "Offer", name: "Premium", price: "289", priceCurrency: "EUR" },
        { "@type": "Offer", name: "Institutionnel", price: "389", priceCurrency: "EUR" },
      ],
    },
  ],
});
